import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiCheck, FiPlay, FiXCircle, FiFilter } from 'react-icons/fi';
import tesoreriaService from '../services/tesoreriaService';
import { proveedorService } from '../services/onebaseService';
import documentosLegalesService from '../services/documentosLegalesService';
import Loading from '../components/Loading';
import './PagosPage.css';

const ensureArray = (v) => Array.isArray(v) ? v : [];

const PagosPage = () => {
  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPago, setEditingPago] = useState(null);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [documentosPendientes, setDocumentosPendientes] = useState([]);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroMetodo, setFiltroMetodo] = useState('');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');

  const [form, setForm] = useState({
    tipo: 'egreso', cuenta_bancaria: '', metodo_pago: 'transferencia',
    numero_comprobante: '', fecha_pago: '', monto: '', proveedor: '',
    cliente: '', glosa: '', documento_legal: '', monto_aplicado: '',
  });

  useEffect(() => { cargarDatos(); }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [pagosRes, cuentasRes, provRes] = await Promise.all([
        tesoreriaService.pagos.getAll(),
        tesoreriaService.cuentasBancarias.getAll({ activo: true }),
        proveedorService.getAll(),
      ]);
      setPagos(ensureArray(pagosRes.data.results || pagosRes.data));
      setCuentasBancarias(ensureArray(cuentasRes.data.results || cuentasRes.data));
      setProveedores(ensureArray(provRes.data.results || provRes.data));
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  };

  const cargarPagos = async () => {
    try {
      const filters = {};
      if (filtroTipo) filters.tipo = filtroTipo;
      if (filtroEstado) filters.estado = filtroEstado;
      if (filtroMetodo) filters.metodo_pago = filtroMetodo;
      if (filtroFechaDesde) filters.fecha_desde = filtroFechaDesde;
      if (filtroFechaHasta) filters.fecha_hasta = filtroFechaHasta;
      const res = await tesoreriaService.pagos.getAll(filters);
      setPagos(ensureArray(res.data.results || res.data));
    } catch { toast.error('Error al cargar pagos'); }
  };

  const cargarDocumentosProveedor = async (proveedorId) => {
    if (!proveedorId) { setDocumentosPendientes([]); return; }
    try {
      const res = await documentosLegalesService.getAll({
        proveedor: proveedorId,
        tipo_documento__in: 'factura_compra,boleta_honorarios,factura_exenta,nota_debito',
        estado__in: 'emitida,vencida',
      });
      setDocumentosPendientes(ensureArray(res.data.results || res.data));
    } catch { setDocumentosPendientes([]); }
  };

  useEffect(() => { if (!loading) cargarPagos(); }, [filtroTipo, filtroEstado, filtroMetodo, filtroFechaDesde, filtroFechaHasta]);

  const handleSave = async () => {
    try {
      const data = {
        tipo: form.tipo,
        cuenta_bancaria: form.cuenta_bancaria,
        metodo_pago: form.metodo_pago,
        numero_comprobante: form.numero_comprobante,
        fecha_pago: form.fecha_pago,
        monto: form.monto,
        glosa: form.glosa,
      };
      if (form.tipo === 'egreso' && form.proveedor) data.proveedor = form.proveedor;
      if (form.tipo === 'ingreso' && form.cliente) data.cliente = form.cliente;
      if (form.documento_legal && form.monto_aplicado) {
        data.detalles_pago = [{ documento_legal: form.documento_legal, monto_aplicado: form.monto_aplicado }];
      }
      if (editingPago) {
        await tesoreriaService.pagos.update(editingPago.id, data);
        toast.success('Pago actualizado');
      } else {
        await tesoreriaService.pagos.create(data);
        toast.success('Pago creado');
      }
      setModalOpen(false);
      setEditingPago(null);
      cargarPagos();
    } catch (err) {
      const errData = err.response?.data;
      if (errData && typeof errData === 'object') {
        const msgs = Object.entries(errData).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' | ');
        toast.error(msgs);
      } else {
        toast.error(errData?.detail || 'Error al guardar pago');
      }
    }
  };

  const handleAprobar = async (id) => {
    if (!window.confirm('¿Aprobar este pago?')) return;
    try {
      await tesoreriaService.pagos.aprobar(id);
      toast.success('Pago aprobado');
      cargarPagos();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al aprobar'); }
  };

  const handleEjecutar = async (id) => {
    if (!window.confirm('¿Ejecutar este pago? Se generara el asiento contable automaticamente.')) return;
    try {
      const res = await tesoreriaService.pagos.ejecutar(id);
      if (res.data?.asiento_generado) {
        toast.success('Pago ejecutado y asiento contable generado');
      } else {
        toast.warning(res.data?.message || 'Pago ejecutado, pero no se generó asiento contable. Verifique la configuración de la cuenta bancaria.');
      }
      cargarPagos();
    } catch (err) { toast.error(err.response?.data?.error || err.response?.data?.detail || 'Error al ejecutar'); }
  };

  const handleAnular = async (id) => {
    if (!window.confirm('¿Anular este pago?')) return;
    try {
      await tesoreriaService.pagos.anular(id);
      toast.success('Pago anulado');
      cargarPagos();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al anular'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este pago?')) return;
    try {
      await tesoreriaService.pagos.delete(id);
      toast.success('Pago eliminado');
      cargarPagos();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleEdit = (pago) => {
    setEditingPago(pago);
    const provId = pago.proveedor?.id || pago.proveedor || '';
    setForm({
      tipo: pago.tipo, cuenta_bancaria: pago.cuenta_bancaria?.id || pago.cuenta_bancaria,
      metodo_pago: pago.metodo_pago, numero_comprobante: pago.numero_comprobante || '',
      fecha_pago: pago.fecha_pago, monto: pago.monto, proveedor: provId,
      cliente: pago.cliente?.id || pago.cliente || '', glosa: pago.glosa || '',
      documento_legal: '', monto_aplicado: '',
    });
    if (provId) cargarDocumentosProveedor(provId);
    setModalOpen(true);
  };

  const resetForm = () => {
    setForm({ tipo: 'egreso', cuenta_bancaria: '', metodo_pago: 'transferencia', numero_comprobante: '', fecha_pago: '', monto: '', proveedor: '', cliente: '', glosa: '', documento_legal: '', monto_aplicado: '' });
    setDocumentosPendientes([]);
  };

  const handleProveedorChange = (provId) => {
    setForm({ ...form, proveedor: provId, documento_legal: '', monto_aplicado: '' });
    cargarDocumentosProveedor(provId);
  };

  const handleDocumentoChange = (docId) => {
    const doc = documentosPendientes.find(d => String(d.id) === String(docId));
    if (doc) {
      const saldo = parseFloat(doc.saldo_pendiente || doc.monto_total || 0);
      setForm({ ...form, documento_legal: docId, monto_aplicado: saldo, monto: form.monto || saldo });
    } else {
      setForm({ ...form, documento_legal: docId, monto_aplicado: '' });
    }
  };

  const formatCurrency = (value) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value || 0);

  const getEstadoBadge = (estado) => {
    const map = {
      pendiente: 'badge-yellow', aprobado: 'badge-blue',
      ejecutado: 'badge-green', anulado: 'badge-red',
    };
    return map[estado] || 'badge-gray';
  };

  const getTipoBadge = (tipo) => tipo === 'egreso' ? 'badge-red' : 'badge-green';

  if (loading) return <Loading />;

  const totalEgresos = pagos.filter(p => p.tipo === 'egreso' && p.estado !== 'anulado').reduce((s, p) => s + parseFloat(p.monto || 0), 0);
  const totalIngresos = pagos.filter(p => p.tipo === 'ingreso' && p.estado !== 'anulado').reduce((s, p) => s + parseFloat(p.monto || 0), 0);

  return (
    <div className="pagos-page">
      <div className="page-header">
        <h1>Gestion de Pagos</h1>
        <button className="btn-primary" onClick={() => { resetForm(); setEditingPago(null); setModalOpen(true); }}>
          <FiPlus /> Nuevo Pago
        </button>
      </div>

      <div className="estadisticas-grid">
        <div className="stat-card" style={{ borderLeftColor: '#3b82f6' }}>
          <div className="stat-label">Total Pagos</div>
          <div className="stat-value">{pagos.length}</div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#dc2626' }}>
          <div className="stat-label">Egresos</div>
          <div className="stat-value">{formatCurrency(totalEgresos)}</div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#059669' }}>
          <div className="stat-label">Ingresos</div>
          <div className="stat-value">{formatCurrency(totalIngresos)}</div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#f59e0b' }}>
          <div className="stat-label">Pendientes</div>
          <div className="stat-value">{pagos.filter(p => p.estado === 'pendiente').length}</div>
        </div>
      </div>

      <div className="filtros-container">
        <select className="select-filtro" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
          <option value="">Todos los tipos</option>
          <option value="egreso">Egresos</option>
          <option value="ingreso">Ingresos</option>
        </select>
        <select className="select-filtro" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="aprobado">Aprobado</option>
          <option value="ejecutado">Ejecutado</option>
          <option value="anulado">Anulado</option>
        </select>
        <select className="select-filtro" value={filtroMetodo} onChange={e => setFiltroMetodo(e.target.value)}>
          <option value="">Todos los metodos</option>
          <option value="transferencia">Transferencia</option>
          <option value="cheque">Cheque</option>
          <option value="efectivo">Efectivo</option>
          <option value="tarjeta_credito">Tarjeta Credito</option>
          <option value="tarjeta_debito">Tarjeta Debito</option>
          <option value="vale_vista">Vale Vista</option>
        </select>
        <input type="date" className="input-filtro" value={filtroFechaDesde} onChange={e => setFiltroFechaDesde(e.target.value)} placeholder="Desde" />
        <input type="date" className="input-filtro" value={filtroFechaHasta} onChange={e => setFiltroFechaHasta(e.target.value)} placeholder="Hasta" />
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Tipo</th>
              <th>Cuenta Bancaria</th>
              <th>Metodo</th>
              <th>Comprobante</th>
              <th>Destinatario</th>
              <th style={{ textAlign: 'right' }}>Monto</th>
              <th>Estado</th>
              <th style={{ textAlign: 'right' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {pagos.length === 0 ? (
              <tr><td colSpan="9" className="empty-state"><p>No hay pagos registrados</p></td></tr>
            ) : pagos.map(pago => (
              <tr key={pago.id}>
                <td>{pago.fecha_pago}</td>
                <td><span className={`badge ${getTipoBadge(pago.tipo)}`}>{pago.tipo === 'egreso' ? 'Egreso' : 'Ingreso'}</span></td>
                <td>{pago.cuenta_bancaria_nombre || pago.cuenta_bancaria?.nombre || '-'}</td>
                <td>{pago.metodo_pago_display || pago.metodo_pago}</td>
                <td className="numero-comprobante">{pago.numero_comprobante || '-'}</td>
                <td>{pago.tipo === 'egreso' ? (pago.proveedor_nombre || pago.proveedor?.nombre || '-') : (pago.cliente_nombre || pago.cliente?.nombre || '-')}</td>
                <td style={{ textAlign: 'right', fontWeight: 600, fontFamily: "'Courier New', monospace", color: pago.tipo === 'egreso' ? '#dc2626' : '#059669' }}>
                  {formatCurrency(pago.monto)}
                </td>
                <td><span className={`badge ${getEstadoBadge(pago.estado)}`}>{pago.estado_display || pago.estado}</span></td>
                <td>
                  <div className="acciones-cell">
                    {pago.estado === 'pendiente' && (
                      <>
                        <button className="btn-icon btn-success" title="Aprobar" onClick={() => handleAprobar(pago.id)}><FiCheck /></button>
                        <button className="btn-icon" title="Editar" onClick={() => handleEdit(pago)}><FiEdit2 /></button>
                        <button className="btn-icon btn-danger" title="Eliminar" onClick={() => handleDelete(pago.id)}><FiTrash2 /></button>
                      </>
                    )}
                    {pago.estado === 'aprobado' && (
                      <>
                        <button className="btn-icon btn-success" title="Ejecutar" onClick={() => handleEjecutar(pago.id)}><FiPlay /></button>
                        <button className="btn-icon btn-danger" title="Anular" onClick={() => handleAnular(pago.id)}><FiXCircle /></button>
                      </>
                    )}
                    {pago.estado === 'ejecutado' && (
                      <button className="btn-icon btn-danger" title="Anular" onClick={() => handleAnular(pago.id)}><FiXCircle /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Crear/Editar */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>{editingPago ? 'Editar Pago' : 'Nuevo Pago'}</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Tipo *</label>
                <select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })}>
                  <option value="egreso">Egreso (Pago a proveedor)</option>
                  <option value="ingreso">Ingreso (Cobro a cliente)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Cuenta Bancaria *</label>
                <select value={form.cuenta_bancaria} onChange={e => setForm({ ...form, cuenta_bancaria: e.target.value })}>
                  <option value="">Seleccionar...</option>
                  {cuentasBancarias.map(c => <option key={c.id} value={c.id}>{c.nombre} ({c.banco})</option>)}
                </select>
              </div>
              {form.tipo === 'egreso' && (
                <div className="form-group">
                  <label>Proveedor *</label>
                  <select value={form.proveedor} onChange={e => handleProveedorChange(e.target.value)}>
                    <option value="">Seleccionar proveedor...</option>
                    {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre} {p.rut ? `(${p.rut})` : ''}</option>)}
                  </select>
                </div>
              )}
              {form.tipo === 'ingreso' && (
                <div className="form-group">
                  <label>Cliente *</label>
                  <input type="number" value={form.cliente} onChange={e => setForm({ ...form, cliente: e.target.value })} placeholder="ID del cliente" />
                </div>
              )}
              <div className="form-group">
                <label>Metodo de Pago *</label>
                <select value={form.metodo_pago} onChange={e => setForm({ ...form, metodo_pago: e.target.value })}>
                  <option value="transferencia">Transferencia Bancaria</option>
                  <option value="cheque">Cheque</option>
                  <option value="efectivo">Efectivo</option>
                  <option value="tarjeta_credito">Tarjeta de Credito</option>
                  <option value="tarjeta_debito">Tarjeta de Debito</option>
                  <option value="vale_vista">Vale Vista</option>
                </select>
              </div>
              <div className="form-group">
                <label>N° Comprobante</label>
                <input value={form.numero_comprobante} onChange={e => setForm({ ...form, numero_comprobante: e.target.value })} placeholder="N° transferencia, cheque..." />
              </div>
              <div className="form-group">
                <label>Fecha *</label>
                <input type="date" value={form.fecha_pago} onChange={e => setForm({ ...form, fecha_pago: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Monto *</label>
                <input type="number" value={form.monto} onChange={e => setForm({ ...form, monto: e.target.value })} placeholder="0" />
              </div>
              {form.tipo === 'egreso' && form.proveedor && (
                <div className="form-group">
                  <label>Documento Asociado</label>
                  <select value={form.documento_legal} onChange={e => handleDocumentoChange(e.target.value)}>
                    <option value="">Sin documento asociado</option>
                    {documentosPendientes.map(d => (
                      <option key={d.id} value={d.id}>
                        {d.numero_documento} - {formatCurrency(d.saldo_pendiente || d.monto_total)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {form.documento_legal && (
                <div className="form-group">
                  <label>Monto Aplicado al Documento</label>
                  <input type="number" value={form.monto_aplicado} onChange={e => setForm({ ...form, monto_aplicado: e.target.value })} placeholder="0" />
                </div>
              )}
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label>Glosa</label>
                <textarea value={form.glosa} onChange={e => setForm({ ...form, glosa: e.target.value })} rows={3} placeholder="Descripcion del pago..." style={{ padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', resize: 'vertical' }} />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
              <button className="btn-primary" onClick={handleSave}>{editingPago ? 'Actualizar' : 'Crear Pago'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PagosPage;
